import { ifBridgeDefined } from '#src/services/dealNativeBridge'
import {
  USER_ACQUISITION_QUERY_PARAM_MAP,
  getUserAcquisitionAttributes,
  getFacebookAdvancedMatchingDataFromUserTraits
} from '#src/services/tracking'
import { GetSessionIdQuery } from '#src/generated/graphql'
import GetSessionIdDocument from './getSessionId.gql'
import { UserTraits } from './userTraits'
import ApolloClient from 'apollo-client'
import pick from 'lodash/pick'
import values from 'lodash/values'
import size from 'lodash/size'
import config from '#src/config'

export interface TrackingOptions extends SegmentAnalytics.SegmentOpts {
  context?: { [key: string]: string | object }
}

type FacebookTrackingEvent = {
  eventName: string
  customOptions?: {
    [key: string]: string | string[] | number | object | boolean | undefined
  }
}

class BaseAnalytics {
  disableWebTracking: boolean
  apolloClient: ApolloClient<any>
  initializedFacebookPixelIds: Set<string> = new Set()
  facebookDepartmentEventsQueue: FacebookTrackingEvent[]

  constructor(apolloClient: ApolloClient<any>) {
    const urlParams = new URLSearchParams(window.location.search)

    this.facebookDepartmentEventsQueue = []

    this.apolloClient = apolloClient
    this.disableWebTracking =
      window.__IS_BOT__ || window.dealNativeBridge || urlParams.has('notrack')
  }

  public setSessionId(sessionId: string) {
    localStorage.setItem('curated_sid', sessionId)
  }

  public getSessionId(): Promise<string> {
    const localSessionId = localStorage.getItem('curated_sid')

    if (localSessionId) {
      return Promise.resolve(localSessionId)
    } else {
      return this.apolloClient
        .query<GetSessionIdQuery>({
          query: GetSessionIdDocument
        })
        .then(({ data: { session: { id } } }) => {
          this.setSessionId(id)
          return id
        })
    }
  }

  public track(
    event: string,
    properties?: Object,
    options?: TrackingOptions,
    callback?: () => void
  ) {
    // Web tracking
    if (!this.disableWebTracking) {
      this.ready(() => {
        // Extract all the user traits related to user acquisition from Segment's traits store
        const traits = this.getUserTraits()
        const userAcquisitionTraits = pick(traits, values(USER_ACQUISITION_QUERY_PARAM_MAP))

        // ...and send those traits along with each track
        if (size(userAcquisitionTraits) > 0) {
          options = options || {}
          options.context = options.context || {}
          options.context.traits = userAcquisitionTraits
        }

        // Ensure every track has a valid `anonymousId` set
        this.getSessionId().then(sessionId => {
          options = options || {}
          options.anonymousId = sessionId

          if (window.analytics) {
            window.analytics.track(event, properties, options, callback)
          }
        })
      })
    }

    // Native tracking
    ifBridgeDefined(bridge => {
      bridge.sendToNative({
        type: 'analyticsMessage',
        payload: {
          type: 'track',
          event,
          properties
        }
      })
    })
  }

  public trackFacebook(eventName: string, customOptions?: FacebookTrackingEvent['customOptions']) {
    const departmentSlug = getUserAcquisitionAttributes().department
    const defaultPixelId = config.get('facebook.pixels.default')
    const departmentPixelId = config.get(`facebook.pixels.${departmentSlug}`)

    // @ts-ignore Facebook pixel typings are incomplete
    window.fbq('trackSingle', defaultPixelId, eventName, customOptions)

    if (departmentPixelId) {
      // @ts-ignore Facebook pixel typings are incomplete
      window.fbq('trackSingle', departmentPixelId, eventName, customOptions)
    } else {
      this.queueDepartmentFacebookEvent({
        eventName,
        customOptions
      })
    }
  }

  private queueDepartmentFacebookEvent(event: FacebookTrackingEvent) {
    this.facebookDepartmentEventsQueue.push(event)
  }

  public flushDepartmentFacebookEvents(departmentSlug: string) {
    const departmentPixelId = config.get(`facebook.pixels.${departmentSlug}`)

    if (!departmentPixelId) {
      console.warn(
        `Attempted to flush enqueued Facebook tracking events, but no pixel ID was found for ${departmentSlug}`
      )
      return
    }

    let event
    while ((event = this.facebookDepartmentEventsQueue.shift())) {
      // @ts-ignore Facebook pixel typings are incomplete
      window.fbq('trackSingle', departmentPixelId, event.eventName, event.customOptions)
    }
  }

  public identify(
    userId: string,
    traits?: UserTraits,
    options?: SegmentAnalytics.SegmentOpts,
    callback?: () => void
  ) {
    // Web tracking
    if (!this.disableWebTracking) {
      if (window.analytics) {
        window.analytics.identify(userId, traits, options, callback)
      }

      if (traits) {
        this.addUserTraits(traits)
      }
    }

    // Native tracking
    ifBridgeDefined(bridge => {
      bridge.sendToNative({
        type: 'analyticsMessage',
        payload: {
          type: 'identify',
          userId: userId,
          traits: traits
        }
      })
    })
  }

  public reset() {
    // Web reset
    if (!this.disableWebTracking) {
      if (window.analytics) {
        window.analytics.reset()
      }
    }

    // Native reset
    ifBridgeDefined(bridge => {
      bridge.sendToNative({
        type: 'analyticsMessage',
        payload: {
          type: 'reset'
        }
      })
    })
  }

  public page(options?: SegmentAnalytics.SegmentOpts) {
    // Track the pageview
    if (!this.disableWebTracking) {
      // Ensure every page view has a valid `anonymousId` set
      this.getSessionId().then(sessionId => {
        options = options || {}
        options.anonymousId = sessionId

        this.trackFacebook('PageView')

        if (window.analytics) {
          window.analytics.page({}, options)
        }
      })
    }
  }

  public getUserTraits() {
    const stringified = localStorage.getItem('curated_user_traits')
    return stringified ? (JSON.parse(stringified) as UserTraits) : {}
  }

  public addUserTraits(newUserTraits: UserTraits) {
    const existingUserTraits = this.getUserTraits()
    const traits = Object.assign(existingUserTraits, newUserTraits)

    // Re-initialize the pixel with the updated user traits. The Facebook documentation
    //   suggests this is bad practice, or an error, but others say it's supported and
    //   is the only way to add traits to a pixel without refreshing the page (i.e. in
    //   a single page app).
    //
    // See: https://stackoverflow.com/questions/39355191/advanced-matching-with-new-facebook-pixel-in-javascript-web-app
    const facebookAdvancedMatchingData = getFacebookAdvancedMatchingDataFromUserTraits(traits)
    const departmentSlug = getUserAcquisitionAttributes().department
    const defaultPixelId = config.get('facebook.pixels.default')
    const departmentPixelId = config.get(`facebook.pixels.${departmentSlug}`)

    if (!this.initializedFacebookPixelIds.has(defaultPixelId)) {
      this.initializedFacebookPixelIds.add(defaultPixelId)

      // @ts-ignore Facebook pixel typings are incomplete
      window.fbq('init', defaultPixelId, facebookAdvancedMatchingData)
    }

    if (departmentPixelId && !this.initializedFacebookPixelIds.has(departmentPixelId)) {
      this.initializedFacebookPixelIds.add(departmentPixelId)

      // @ts-ignore Facebook pixel typings are incomplete
      window.fbq('init', departmentPixelId, facebookAdvancedMatchingData)
    }

    // Persist to localStorage
    localStorage.setItem('curated_user_traits', JSON.stringify(traits))

    // Persist to Segment when ready
    this.ready(() => {
      if (window.analytics) {
        window.analytics.user().traits(traits)
      }
    })
  }

  public group(args: any) {
    // Ensure the group() method is ready, otherwise return undefined
    if (window.analytics && window.analytics.group) {
      return window.analytics.group.apply(window.analytics, args)
    } else {
      return undefined
    }
  }

  public load(writeKey: string) {
    if (window.analytics) {
      window.analytics.load(writeKey)
    }
  }

  public alias(
    userId: string,
    previousId?: string,
    options?: SegmentAnalytics.SegmentOpts,
    callback?: () => void
  ) {
    if (!this.disableWebTracking && window.analytics) {
      window.analytics.alias(userId, previousId, options, callback)
    }
  }

  public ready(callback: () => void) {
    if (window.analytics) {
      window.analytics.ready(() => {
        callback()
      })
    }
  }

  public debug(state?: boolean) {
    if (window.analytics) {
      window.analytics.debug(state)
    }
  }

  public on(
    event: string,
    callback: {
      (event: string, properties: Object, options: SegmentAnalytics.SegmentOpts): void
    }
  ) {
    if (window.analytics) {
      window.analytics.on(event, callback)
    }
  }

  public timeout(milliseconds: number) {
    if (window.analytics) {
      window.analytics.timeout(milliseconds)
    }
  }
}

export { BaseAnalytics }
